import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  startCapture, stopCapture, recordKeystroke, collectTelemetry, resetCapture,
} from "@/lib/biometrics";
import { Lock, Shield, AlertTriangle, KeyRound, Eye, EyeOff } from "lucide-react";

const Login = () => {
  const { user, login, verifyMfa } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [stage, setStage] = useState("creds"); // creds | mfa
  const [email, setEmail] = useState("admin@eigoo.io");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [riskInfo, setRiskInfo] = useState(null);
  const [mfa, setMfa] = useState({ challenge_token: "", code: "" });

  useEffect(() => {
    startCapture();
    return () => stopCapture();
  }, []);

  useEffect(() => {
    if (user) nav(from, { replace: true });
  }, [user, from, nav]);

  const onSubmitCreds = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const telemetry = collectTelemetry();
      const r = await login({ email, password, telemetry });
      if (r._stage === "mfa") {
        setStage("mfa");
        setMfa({ challenge_token: r.challenge_token, code: "" });
        setRiskInfo({ score: r.risk_score, reasons: r.reasons || [] });
        resetCapture();
      }
    } catch (err) {
      setError(err.message || "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const telemetry = collectTelemetry();
      await verifyMfa({ challenge_token: mfa.challenge_token, code: mfa.code, telemetry });
    } catch (err) {
      setError(err.message || "MFA verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      data-testid="login-page"
    >
      {/* ambient lattice */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20" preserveAspectRatio="none">
        <defs>
          <pattern id="lattice" width="80" height="80" patternUnits="userSpaceOnUse">
            <path d="M 80 0 L 0 0 0 80" fill="none" stroke="rgba(200,162,75,0.18)" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#lattice)" />
      </svg>

      <div className="w-full max-w-[460px] relative z-10 fade-up">
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center">
            <span className="sov-sigil mb-4" style={{ width: 32, height: 32 }} />
            <div className="font-display text-[22px] tracking-[0.4em] text-gold-100 gold-glow">
              EIGOO
            </div>
            <div className="font-mono text-[10px] tracking-[0.42em] text-ivory-3 mt-1">
              TECH · OPSSA
            </div>
          </div>
          <div className="sov-divider mt-6">
            <span className="font-mono text-[9px] tracking-[0.3em] text-gold-300 uppercase">
              Sovereign Audit Cell
            </span>
          </div>
          <p className="font-serif italic text-[14px] text-ivory-2 mt-4">
            "Vigilance is the toll of sovereignty."
          </p>
        </div>

        <div className="sov-panel sov-corners p-7 relative">
          <span className="corner tl" /><span className="corner tr" />
          <span className="corner bl" /><span className="corner br" />

          {stage === "creds" && (
            <form onSubmit={onSubmitCreds} className="space-y-4" data-testid="login-form">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={12} className="text-gold-300" />
                <h2 className="font-display text-[13px] tracking-[0.22em] text-gold-100 uppercase">
                  Operator Authentication
                </h2>
              </div>

              <div>
                <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Coordinate (Email)</label>
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  className="sov-input mt-1"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                />
              </div>

              <div>
                <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Cipher (Password)</label>
                <div className="relative">
                  <input
                    data-testid="login-password"
                    type={showPwd ? "text" : "password"}
                    required
                    className="sov-input mt-1 pr-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={() => recordKeystroke()}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 mt-0.5 -translate-y-1/2 text-ivory-3 hover:text-gold-200"
                    data-testid="toggle-password"
                  >
                    {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="border border-[rgba(255,122,110,0.4)] bg-[rgba(200,68,59,0.08)] px-3 py-2 flex items-start gap-2" data-testid="login-error">
                  <AlertTriangle size={12} className="mt-1 shrink-0" style={{ color: "#ff7a6e" }} />
                  <span className="font-mono text-[11px]" style={{ color: "#ffaba2" }}>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full justify-center mt-2"
                data-testid="login-submit"
              >
                {submitting ? "Authenticating…" : "Board the Vessel"}
              </button>

              <div className="pt-3 mt-1 border-t border-[color:var(--gold-line)]">
                <div className="flex items-center gap-2 text-ivory-3">
                  <Shield size={11} className="text-gold-300" />
                  <span className="font-mono text-[9px] tracking-[0.18em] uppercase">
                    Biometric · Device · Geo · Cadence captured for risk scoring
                  </span>
                </div>
              </div>
            </form>
          )}

          {stage === "mfa" && (
            <form onSubmit={onSubmitMfa} className="space-y-4" data-testid="mfa-form">
              <div className="flex items-center gap-2 mb-2">
                <KeyRound size={12} className="text-gold-300" />
                <h2 className="font-display text-[13px] tracking-[0.22em] text-gold-100 uppercase">
                  Step-Up Verification
                </h2>
              </div>

              {riskInfo && (
                <div className="border border-[color:var(--gold-line)] bg-[rgba(200,162,75,0.05)] p-3" data-testid="risk-disclosure">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] tracking-[0.18em] text-ivory-3 uppercase">
                      Risk Score
                    </span>
                    <span
                      className="font-display text-[18px]"
                      style={{
                        color: riskInfo.score < 31 ? "#dcb95f" : riskInfo.score < 71 ? "#f5a73a" : "#ff7a6e",
                      }}
                    >
                      {riskInfo.score}/100
                    </span>
                  </div>
                  <div className="font-serif italic text-[12px] text-ivory-2 mt-2">
                    Sentinel flagged signals warranting a second factor:
                  </div>
                  <ul className="mt-2 space-y-1">
                    {riskInfo.reasons.map((r, i) => (
                      <li key={i} className="flex items-center gap-2 font-mono text-[10px] text-ivory-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold-300)]" />
                        {r.label} <span className="text-ivory-3">+{r.weight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">
                  TOTP Code (Authenticator App)
                </label>
                <input
                  data-testid="mfa-code"
                  type="text"
                  pattern="[0-9]{6}"
                  inputMode="numeric"
                  required
                  className="sov-input mt-1 font-mono text-center tracking-[0.5em] text-[20px]"
                  value={mfa.code}
                  onChange={(e) => setMfa({ ...mfa, code: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              {error && (
                <div className="border border-[rgba(255,122,110,0.4)] bg-[rgba(200,68,59,0.08)] px-3 py-2 flex items-start gap-2">
                  <AlertTriangle size={12} className="mt-1 shrink-0" style={{ color: "#ff7a6e" }} />
                  <span className="font-mono text-[11px]" style={{ color: "#ffaba2" }}>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || mfa.code.length !== 6}
                className="btn-gold w-full justify-center"
                data-testid="mfa-submit"
              >
                {submitting ? "Verifying…" : "Confirm Sovereign Identity"}
              </button>
              <button
                type="button"
                onClick={() => { setStage("creds"); setError(""); setRiskInfo(null); }}
                className="btn-ghost w-full justify-center"
                data-testid="mfa-back"
              >
                Re-enter credentials
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-6 font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">
          Vessel I.0 · Audit Cell · Classified · Sovereign
        </div>
      </div>
    </div>
  );
};

export default Login;
